import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BackOfficeSurveySettings, BackOfficeSurveySettingsDTO } from "./backOfficeSurveySettings";
import { SurveyStatus } from "../types/surveyStatus";
import { ThemeWithHexCodes, ThemeWithHexCodesDTO } from "./themeWithHexCodes";
import { RespondentColumnDetails, RespondentColumnDetailsDTO } from "../types/respondentColumnDetails";
/* eslint-disable import/export */
export abstract class GetSurveySettingsRPC implements
  LeoRPC<GetSurveySettingsRPC.Request, GetSurveySettingsRPC.Response, GetSurveySettingsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveySettingsRPC.Request
  ): Promise<LeoRPCResult<GetSurveySettingsRPC.Response, GetSurveySettingsRPC.Errors.Errors>>;
}

export namespace GetSurveySettingsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveySettingsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveySettingsRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    backOfficeSurveySettings: BackOfficeSurveySettingsDTO;
    surveyStatus: SurveyStatus.SurveyStatusDTO;
    supportedFonts: string[];
    supportedThemes: ThemeWithHexCodesDTO[];
    respondentColumnDetails: RespondentColumnDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveySettingsResponse: boolean = true;
  
    readonly backOfficeSurveySettings: BackOfficeSurveySettings;
    readonly surveyStatus: SurveyStatus.SurveyStatus;
    readonly supportedFonts: string[];
    readonly supportedThemes: ThemeWithHexCodes[];
    readonly respondentColumnDetails: RespondentColumnDetails[];
  
  
    constructor(
    backOfficeSurveySettings: BackOfficeSurveySettings, 
    surveyStatus: SurveyStatus.SurveyStatus, 
    supportedFonts: string[], 
    supportedThemes: ThemeWithHexCodes[], 
    respondentColumnDetails: RespondentColumnDetails[]
    ) {
      this.backOfficeSurveySettings = backOfficeSurveySettings;
      this.surveyStatus = surveyStatus;
      this.supportedFonts = supportedFonts;
      this.supportedThemes = supportedThemes;
      this.respondentColumnDetails = respondentColumnDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const backOfficeSurveySettings = BackOfficeSurveySettings.fromDTO(getObject(dto, "backOfficeSurveySettings", InvalidResponseBackOfficeSurveySettingsError));
      const surveyStatus = SurveyStatus.fromDTO(getObject(dto, "surveyStatus", InvalidResponseSurveyStatusError));
      const supportedFonts = getList<string, string>(
        dto,
        "supportedFonts",
        (e: string) => e,
        InvalidResponseSupportedFontsError
      );
      const supportedThemes = getList<ThemeWithHexCodesDTO, ThemeWithHexCodes>(
        dto,
        "supportedThemes",
        ThemeWithHexCodes.fromDTO,
        InvalidResponseSupportedThemesError
      );
      const respondentColumnDetails = getList<RespondentColumnDetailsDTO, RespondentColumnDetails>(
        dto,
        "respondentColumnDetails",
        RespondentColumnDetails.fromDTO,
        InvalidResponseRespondentColumnDetailsError
      );
      return new Response(
        backOfficeSurveySettings,
        surveyStatus,
        supportedFonts,
        supportedThemes,
        respondentColumnDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          backOfficeSurveySettings: this.backOfficeSurveySettings.toDTO(),
          surveyStatus: SurveyStatus.toDTO(this.surveyStatus),
          supportedFonts: this.supportedFonts,
          supportedThemes: this.supportedThemes.map((e: ThemeWithHexCodes) => { return e.toDTO() }),
          respondentColumnDetails: this.respondentColumnDetails.map((e: RespondentColumnDetails) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveySettings(backOfficeSurveySettings='${this.backOfficeSurveySettings}',surveyStatus='${this.surveyStatus}',supportedFonts='${this.supportedFonts}',supportedThemes='${this.supportedThemes}',respondentColumnDetails='${this.respondentColumnDetails}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveySettingsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetSurveySettingsRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveySettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GetSurveySettingsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseBackOfficeSurveySettingsError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBackOfficeSurveySettingsError";
  }
}

export class InvalidResponseSurveyStatusError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyStatusError";
  }
}

export class InvalidResponseSupportedFontsError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSupportedFontsError";
  }
}

export class InvalidResponseSupportedThemesError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSupportedThemesError";
  }
}

export class InvalidResponseRespondentColumnDetailsError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentColumnDetailsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetSurveySettingsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

